import React, { useEffect, useRef, useState } from 'react';
import { useViewport } from '@xyflow/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IRelativeCondition } from '../../../DataSet/types/BottomBarInterfaces';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Box,
  Skeleton,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { parseISO } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useDispatch, useSelector } from 'react-redux';
import { setRelativeCondition } from '../../../../redux/Calculations/CalculationsActions';
import { formatDateToYYYYMMDD } from '../../utils';
import { useRelaiveFilter } from '../../../../hooks/useRelativeFilter';
import { RootState } from '../../../../redux';
import { IField, IFlow } from './EvaluationInterface';
import {
  AnchorDatePatternRelativeFilterCollections,
  datePatternRelativeFilterCollections,
  RelativeFilterPatternCollections,
} from '../../../FilterUtils/FilterPatternCollection';
interface RelativeFilterProps {
  propKey: string;
  conditionFilterId: string;
  filterIdx: number;
  fieldDetails: IField | null;
  calculationDetails: IFlow | null;
  relativeCondition: IRelativeCondition | null;
  specificDate?: string;
  disabled?: boolean;
  isFlow: boolean;
  dataType: string | null;
}
type TEventType =
  | 'from-input'
  | 'to-input'
  | 'anchorDate'
  | 'from-timeGrain'
  | 'to-timeGrain'
  | 'from-relative'
  | 'to-relative';
const RelativeFilter = ({
  propKey,
  conditionFilterId,
  filterIdx,
  relativeCondition,
  disabled = false,
  fieldDetails,
  calculationDetails,
  isFlow,
  dataType,
}: RelativeFilterProps) => {
  const dispatch = useDispatch();
  const { zoom } = useViewport();
  const { dsInfo } = useSelector((state: RootState) => ({
    dsInfo: state.chartProperties.properties[propKey]?.selectedDs as {
      connectionId: string;
      id: string;
      datasetName: string;
      isFlatFileDataset: boolean;
      workSpaceId: string;
    },
  }));
  const [_relativeCondition, _setRelativeCondition] = useState(
    relativeCondition ?? {
      from: ['last', '1', 'year'],
      to: ['next', '1', 'year'],
      anchorDate: 'today',
    },
  );
  useEffect(() => {
    _setRelativeCondition(
      relativeCondition ?? {
        from: ['last', '1', 'year'],
        to: ['next', '1', 'year'],
        anchorDate: 'today',
      },
    );
  }, [relativeCondition]);
  const [fromInput, setFromInput] = useState<string>(String(_relativeCondition.from[1]));
  const [toInput, setToInput] = useState<string>(String(_relativeCondition.to[1]));
  const calcDetails = useRef(calculationDetails);

  const {
    loading,
    result: date,
    error,
  } = useRelaiveFilter(
    dsInfo.connectionId,
    dsInfo.id,
    dsInfo.workSpaceId,
    calcDetails.current,
    _relativeCondition,
    isFlow ? Object.values(calcDetails.current?.fields!)[0].tableId : fieldDetails?.tableId,
    isFlow ? calcDetails.current?.calculatedFieldName : fieldDetails?.fieldName,
    isFlow,
    dataType!,
  );
  const handleChange = (type: TEventType, value: string) => {
    if (disabled) return;
    const newRelatievCondiion = {
      ..._relativeCondition,
    };
    switch (type) {
      case 'from-input':
        newRelatievCondiion.from[1] = value;
        break;
      case 'to-input':
        newRelatievCondiion.to[1] = value;
        break;
      case 'anchorDate':
        newRelatievCondiion.anchorDate = value;
        break;
      case 'from-timeGrain':
        newRelatievCondiion.from[2] = value;
        break;
      case 'to-timeGrain':
        newRelatievCondiion.to[2] = value;
        break;
      case 'from-relative':
        newRelatievCondiion.from[0] = value;
        break;
      case 'to-relative':
        newRelatievCondiion.to[0] = value;
        break;
    }
    dispatch(setRelativeCondition(propKey, conditionFilterId, filterIdx, newRelatievCondiion));
  };

  return (
    <Box>
      <Box
        id="relative-filter-from"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.3rem',
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.2rem',
          }}
        >
          From{' '}
          {loading ? (
            <Skeleton variant="text" sx={{ fontSize: '0.7rem' }} width="50%" />
          ) : error ? (
            `${error.message}`
          ) : (
            `(${date.from})`
          )}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FormControl
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#2bb9bb',
                },
                '&:hover fieldset': {
                  borderColor: '#2bb9bb',
                },
              },
            }}
          >
            <Select
              IconComponent={KeyboardArrowDownIcon}
              value={_relativeCondition.from[0] as string}
              sx={{
                height: '1.5rem',
              }}
              onChange={(e: SelectChangeEvent) => {
                if (disabled) return;
                handleChange('from-relative', e.target.value);
              }}
              disabled={disabled}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      transform: `scale(${zoom}) !important`
                    }
                  }
                },
              }}
            >
              {RelativeFilterPatternCollections.map((item) => (
                <MenuItem
                  key={item.key}
                  value={item.key}
                  selected={item.key === _relativeCondition.from[0]}
                >
                  <Typography
                    sx={{
                      width: 'auto',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '12px',
                      lineHeight: '20px',
                    }}
                  >
                    {item.value}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{
              display: _relativeCondition.from[0] === 'current' ? 'none' : 'flex',
              flex: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#2bb9bb',
                },
                '&:hover fieldset': {
                  borderColor: '#2bb9bb',
                },
              },
            }}
            disabled={disabled}
            value={fromInput}
            InputProps={{
              style: {
                height: '25px',
              },
            }}
            onChange={(e) => {
              if (disabled) return;
              const value = e.target.value;
              if (!/^\d+$/.test(value)) return;
              setFromInput(value);
            }}
            onBlur={(e) => {
              if (disabled) return;
              const value = e.target.value;
              if (!/^\d+$/.test(value)) return;
              handleChange('from-input', value);
            }}
          />
        </Box>
        <FormControl
          fullWidth
          size="small"
          sx={{
            marginTop: '0.3rem',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#2bb9bb',
              },
              '&:hover fieldset': {
                borderColor: '#2bb9bb',
              },
            },
          }}
        >
          <Select
            value={_relativeCondition.from[2] as string}
            sx={{
              height: '1.5rem',
            }}
            IconComponent={KeyboardArrowDownIcon}
            onChange={(e: SelectChangeEvent) => {
              if (disabled) return;
              handleChange('from-timeGrain', e.target.value);
            }}
            disabled={disabled}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    transform: `scale(${zoom}) !important`
                  }
                }
              },
            }}
          >
            {datePatternRelativeFilterCollections.map((item) => (
              <MenuItem
                key={item.key}
                value={item.key}
                selected={item.key === _relativeCondition.from[2]}
              >
                <Typography
                  sx={{
                    width: 'auto',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '12px',
                    lineHeight: '20px',
                  }}
                >
                  {item.value}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        id="relative-filter-to"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.3rem',
          marginTop: '0.3rem',
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.2rem',
          }}
        >
          To{' '}
          {loading ? (
            <Skeleton variant="text" sx={{ fontSize: '0.7rem' }} width="50%" />
          ) : (
            `(${date.to})`
          )}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FormControl
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#2bb9bb',
                },
                '&:hover fieldset': {
                  borderColor: '#2bb9bb',
                },
              },
            }}
          >
            <Select
              IconComponent={KeyboardArrowDownIcon}
              value={_relativeCondition.to[0] as string}
              sx={{
                height: '1.5rem',
              }}
              onChange={(e: SelectChangeEvent) => {
                if (disabled) return;
                handleChange('to-relative', e.target.value);
              }}
              disabled={disabled}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      transform: `scale(${zoom}) !important`
                    }
                  }
                },
              }}
            >
              {RelativeFilterPatternCollections.map((item) => (
                <MenuItem
                  key={item.key}
                  value={item.key}
                  selected={item.key === _relativeCondition.to[0]}
                >
                  <Typography
                    sx={{
                      width: 'auto',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '12px',
                      lineHeight: '20px',
                    }}
                  >
                    {item.value}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{
              display: _relativeCondition.to[0] === 'current' ? 'none' : 'flex',
              flex: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#2bb9bb',
                },
                '&:hover fieldset': {
                  borderColor: '#2bb9bb',
                },
              },
            }}
            onChange={(e) => {
              if (disabled) return;
              const value = e.target.value;
              if (!/^\d+$/.test(value)) return;
              setToInput(value);
            }}
            onBlur={(e) => {
              if (disabled) return;
              const value = e.target.value;
              if (!/^\d+$/.test(value)) return;
              handleChange('from-input', value);
            }}
            disabled={disabled}
            value={toInput}
            InputProps={{
              style: {
                height: '25px',
              },
            }}
          />
        </Box>
        <FormControl
          fullWidth
          size="small"
          sx={{
            marginTop: '0.3rem',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#2bb9bb',
              },
              '&:hover fieldset': {
                borderColor: '#2bb9bb',
              },
            },
          }}
        >
          <Select
            value={_relativeCondition.to[2] as string}
            sx={{
              height: '1.5rem',
            }}
            IconComponent={KeyboardArrowDownIcon}
            onChange={(e: SelectChangeEvent) => {
              if (disabled) return;
              handleChange('to-timeGrain', e.target.value);
            }}
            disabled={disabled}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    transform: `scale(${zoom}) !important`
                  }
                }
              },
            }}
          >
            {datePatternRelativeFilterCollections.map((item) => (
              <MenuItem
                key={item.key}
                value={item.key}
                selected={item.key === _relativeCondition.to[2]}
              >
                <Typography
                  sx={{
                    width: 'auto',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '12px',
                    lineHeight: '20px',
                  }}
                >
                  {item.value}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        id="relative-filter-anchor-date"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.3rem',
          marginTop: '0.3rem',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Based on Date
        </Typography>
        <FormControl
          fullWidth
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#2bb9bb',
              },
              '&:hover fieldset': {
                borderColor: '#2bb9bb',
              },
            },
          }}
        >
          <Select
            disabled={disabled}
            value={
              _relativeCondition.anchorDate === '' ||
                /^\d{4}-\d{2}-\d{2}$/.test(_relativeCondition.anchorDate)
                ? 'specificDate'
                : _relativeCondition.anchorDate
            }
            sx={{
              height: '1.5rem',
            }}
            IconComponent={KeyboardArrowDownIcon}
            onChange={(e: SelectChangeEvent) => {
              if (disabled) return;
              handleChange(
                'anchorDate',
                e.target.value === 'specificDate'
                  ? formatDateToYYYYMMDD(new Date().toISOString())
                  : e.target.value,
              );
            }}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    transform: `scale(${zoom}) !important`
                  }
                }
              },
            }}
          >
            {AnchorDatePatternRelativeFilterCollections.map((item) => (
              <MenuItem
                key={item.key}
                value={item.key}
                selected={
                  item.key ===
                  (/^\d{4}-\d{2}-\d{2}$/.test(_relativeCondition.anchorDate)
                    ? 'specificDate'
                    : _relativeCondition.anchorDate)
                }
              >
                <Typography
                  sx={{
                    width: 'auto',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '12px',
                    lineHeight: '20px',
                  }}
                >
                  {item.value}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(_relativeCondition.anchorDate === '' ||
          /^\d{4}-\d{2}-\d{2}$/.test(_relativeCondition.anchorDate)) && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                enableAccessibleFieldDOMStructure={false}
                value={parseISO(
                  _relativeCondition.anchorDate !== ''
                    ? _relativeCondition.anchorDate
                    : new Date().toISOString(),
                )}
                sx={{
                  width: '100%',
                  marginTop: '0.3rem',
                  transform: `scale(0.5) !important`
                }}
                onChange={(newValue) => {
                  if (!newValue || disabled) return;
                  const modifiedNewVal = formatDateToYYYYMMDD(newValue);
                  handleChange('anchorDate', modifiedNewVal);
                }}
                slots={{
                  textField: (params) => (
                    <TextField
                      disabled={disabled}
                      {...params}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#2bb9bb',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2bb9bb',
                          },
                        },
                      }}
                      className={`customDatePickerHeight `}
                    />
                  ),
                }}
              />
            </LocalizationProvider>
          )}
      </Box>
    </Box>
  );
};

export default RelativeFilter;
